import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import './Tournaments.css';

/* ─────────────────────────────────────────────────────────────
   TOURNAMENTS LIST PAGE
   Shows tournaments you created and tournaments you're in.
   ───────────────────────────────────────────────────────────── */
function Tournaments() {
  const { user } = useAuth();

  const [myTournaments,  setMyTournaments]  = useState([]);
  const [inTournaments,  setInTournaments]  = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [activeTab,      setActiveTab]      = useState('created');

  useEffect(() => {
    if (!user) return;
    loadTournaments();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadTournaments() {
    setLoading(true);

    // Tournaments I created
    const { data: created } = await supabase
      .from('tournaments')
      .select('*')
      .eq('creator_id', user.id)
      .order('created_at', { ascending: false });

    // Tournaments I'm a participant in (singles) or team member of (doubles)
    const [{ data: participantRows }, { data: teamRows }] = await Promise.all([
      supabase.from('tournament_players').select('tournament_id').eq('player_id', user.id),
      supabase.from('tournament_teams')
        .select('tournament_id')
        .or(`player1_id.eq.${user.id},player2_id.eq.${user.id}`),
    ]);

    const createdIds = new Set((created || []).map(t => t.id));
    const participantIds = [
      ...(participantRows || []).map(r => r.tournament_id),
      ...(teamRows || []).map(r => r.tournament_id),
    ].filter((id, idx, arr) => !createdIds.has(id) && arr.indexOf(id) === idx);

    let participating = [];
    if (participantIds.length) {
      const { data } = await supabase
        .from('tournaments')
        .select('*')
        .in('id', participantIds)
        .order('created_at', { ascending: false });
      participating = data || [];
    }

    setMyTournaments(created || []);
    setInTournaments(participating);
    setLoading(false);
  }

  const formatLabel  = { bo1: 'Best of 1', bo3: 'Best of 3', bo5: 'Best of 5', bo7: 'Best of 7' };
  const statusLabel  = { active: 'In Progress', completed: 'Completed' };
  const statusClass  = { active: 'trn-badge--active', completed: 'trn-badge--done' };

  function TournamentRow({ t }) {
    return (
      <Link to={`/tournaments/${t.id}`} className="trn-row">
        <div className="trn-row-main">
          <span className="trn-row-name">{t.name}</span>
          <div className="trn-row-badges">
            <span className="trn-type-badge">
              {t.tournament_type === 'round_robin' ? 'Round Robin' : 'Single Elim'}
            </span>
            {t.is_doubles && <span className="trn-badge trn-badge--2v2">2v2</span>}
            <span className={`trn-badge ${statusClass[t.status]}`}>{statusLabel[t.status]}</span>
          </div>
        </div>
        <div className="trn-row-meta">
          <span>{t.player_count} {t.is_doubles ? 'teams' : 'players'}</span>
          <span>·</span>
          <span>{formatLabel[t.format] || t.format}</span>
          <span>·</span>
          <span>{t.counts_towards_elo ? 'Ranked' : 'Unranked'}</span>
          <span>·</span>
          <span>{new Date(t.created_at).toLocaleDateString()}</span>
        </div>
      </Link>
    );
  }

  if (loading) {
    return (
      <div className="trn-page">
        <div className="trn-container">
          <div className="trn-loading">
            <span className="trn-loading-star">★</span>
            <span className="trn-loading-star">★</span>
            <span className="trn-loading-star">★</span>
          </div>
        </div>
      </div>
    );
  }

  const noTournaments = myTournaments.length === 0 && inTournaments.length === 0;

  return (
    <div className="trn-page">
      <div className="trn-container">
        <div className="trn-header">
          <h1 className="trn-title">Tournaments</h1>
          <Link to="/tournaments/new" className="trn-create-btn">+ Create Tournament</Link>
        </div>

        {noTournaments ? (
          <div className="trn-empty">
            <p className="trn-empty-title">No tournaments yet</p>
            <p className="trn-empty-sub">Create one and invite your friends to compete.</p>
            <Link to="/tournaments/new" className="trn-create-btn">+ Create Tournament</Link>
          </div>
        ) : (
          <>
            <div className="trn-tabs">
              <button
                className={`trn-tab ${activeTab === 'created' ? 'active' : ''}`}
                onClick={() => setActiveTab('created')}
              >
                Created {myTournaments.length > 0 && <span className="trn-tab-count">{myTournaments.length}</span>}
              </button>
              <button
                className={`trn-tab ${activeTab === 'playing' ? 'active' : ''}`}
                onClick={() => setActiveTab('playing')}
              >
                Playing In {inTournaments.length > 0 && <span className="trn-tab-count">{inTournaments.length}</span>}
              </button>
            </div>

            {activeTab === 'created' && (
              <div className="trn-list">
                {myTournaments.length === 0 ? (
                  <p className="trn-list-empty">You haven't created any tournaments yet.</p>
                ) : (
                  myTournaments.map(t => <TournamentRow key={t.id} t={t} />)
                )}
              </div>
            )}

            {activeTab === 'playing' && (
              <div className="trn-list">
                {inTournaments.length === 0 ? (
                  <p className="trn-list-empty">You haven't been added to any tournaments yet.</p>
                ) : (
                  inTournaments.map(t => <TournamentRow key={t.id} t={t} />)
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Tournaments;
